import { AfterViewChecked, ChangeDetectorRef, Directive, OnDestroy } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { EshavaFormControl, EshavaFormControlConfiguration, EshavaFormControlValidationConfiguration } from 'eshava-controls';
import { catchError, delay, map, Observable, of, Subject, Subscription, switchMap } from 'rxjs';

import { errorMessageConstants } from '../constants';
import { BannerMessageLevel } from '../enums';
import { BannerMessage, BaseUniqueResponse, ErrorResponse, EshavaScrollSpyItem } from '../models';
import { TranslationService } from '../services';

@Directive()
export abstract class AbstractEditDialogComponent implements OnDestroy, AfterViewChecked {
  protected _subscriptions: Subscription = new Subscription();
  protected _loadingCounter = 0;
  protected _controlConfigurations: EshavaFormControlValidationConfiguration[] | undefined;
  protected _controls: EshavaFormControl[] = [];

  public subjectDisableStateChanged: Subject<boolean> = new Subject<boolean>();
  public subjectReadonlyStateChanged: Subject<boolean> = new Subject<boolean>();
  public hasLoadingError = false;
  public showLoadingIndicator = true;
  public isCreateDialog = true;
  public formConfiguration: EshavaFormControlConfiguration | undefined;
  public errorMessage: BannerMessage | undefined;

  constructor(
    initialLoadingCounter: number,
    private _cdr: ChangeDetectorRef,
    protected _translationGroup: string,
    protected _translationService: TranslationService,
    public scrollSpyItems: EshavaScrollSpyItem[]
  ) {
    this._loadingCounter = initialLoadingCounter;
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public ngAfterViewChecked(): void {
    this._cdr.detectChanges();
  }

  protected abstract createControl(): void;

  protected loadSomthing<T>(translationKey: string, loadAsync: Observable<T[]>, setResult: (values: T[]) => void): void {
    this._subscriptions.add(loadAsync.subscribe({
      next: response => {
        setResult(response);

        this._loadingCounter--;
        this.showLoadingIndicator = this._loadingCounter > 0;
        this.createControl();
      },
      error: (errorResponse: ErrorResponse) => {
        this.errorMessage = {
          level: errorResponse.status! >= 500 ? BannerMessageLevel.critical : BannerMessageLevel.error,
          title: this._translationService.translate(translationKey, this._translationGroup),
          text1: this._translationService.translate(errorResponse.error?.message || errorMessageConstants.readDataError, 'errors')
        };

        this.hasLoadingError = true;
        this._loadingCounter--;
        this.showLoadingIndicator = this._loadingCounter > 0;
      }
    }));
  }

  protected validateUniqueness(control: AbstractControl, unique: (name: string) => Observable<BaseUniqueResponse>, errorTitle: string): Observable<ValidationErrors | null> {
    return of(control.value?.toString())
      .pipe(
        delay(300),
        switchMap(name => !name
          ? of(null)
          : unique(name)
            .pipe(
              map(response => response.unique ? null : { notUnique: true }),
              catchError((errorResponse: ErrorResponse) => {
                this.errorMessage = {
                  level: errorResponse.status! >= 500 ? BannerMessageLevel.critical : BannerMessageLevel.error,
                  title: this._translationService.translate(errorTitle, this._translationGroup),
                  text1: this._translationService.translate(errorResponse.error?.message || errorMessageConstants.readDataError, 'errors')
                };

                return of({ notUnique: true });
              })
            ))
      );
  }

  protected checkFormValidationState(formConfiguration: EshavaFormControlConfiguration, callback: () => void, timeDelay: number = 300): boolean {
    if (!formConfiguration!.formGroup.valid && !formConfiguration!.formGroup.invalid) {
      setTimeout(() => callback(), timeDelay);

      return false;
    }

    return true;
  }

  protected checkFormValidationStates(formConfigurations: EshavaFormControlConfiguration[], callback: () => void, timeDelay: number = 300): boolean {
    for (const formConfiguration of formConfigurations) {
      if (!this.checkFormValidationState(formConfiguration, callback, timeDelay)) {
        return false;
      }
    }

    return true;
  }
}
